import { PostHogProvider, usePostHog } from "@posthog/react";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  redirect,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { registerAgentTools } from "../lib/agents/webmcp";
import {
  aiReferrerOf,
  analyticsEnabled,
  POSTHOG_TOKEN,
  posthogOptions,
} from "../lib/analytics";
import { arrivedViaLlms } from "../lib/llmsFragment";
import { documentHeaders, documentLang } from "../lib/seo";
import { collectVitals } from "../lib/vitals";
import appCss from "../styles.css?url";

interface MyRouterContext {
  queryClient: QueryClient;
}

/**
 * The fallback title and description, for the handful of responses that reach
 * here without a route of their own — chiefly the 404. Every real page sets its
 * own from its own language bundle.
 *
 * These described the reference app ("pray to unlock your apps"), which is what
 * a crawler saw on any URL that did not match a route.
 */
const TITLE = "Fazly — AI face analysis and glow-up coach";
const DESCRIPTION =
  "Free on-device face scoring tools and a glow-up coach. Measure symmetry, face shape, jawline and canthal tilt in your browser — nothing is uploaded.";

export const Route = createRootRouteWithContext<MyRouterContext>()({
  /**
   * The one thing that has to happen before any route matches: get the visitor
   * onto the canonical hostname.
   *
   * Server-only, and the dynamic import is what keeps it that way — the module
   * it pulls in reads the incoming request, which does not exist in a browser,
   * and a static import would drag it into the client graph. `beforeLoad` runs
   * on both sides, so the guard is not optional.
   */
  beforeLoad: async ({ location }) => {
    if (!import.meta.env.SSR) return;
    // The v1 site's 125 URLs, before anything else: they are real inbound
    // links and a 404 discards them. See lib/legacyPaths.ts.
    //
    // Here on the root rather than as a catch-all route, because a catch-all
    // would have to rank against every real route and TanStack resolves it
    // last — which is correct for a 404 page and wrong for a redirect that has
    // to fire for `/glossary` while `/glossary/mewing` is not a route at all.
    const { legacyRedirect } = await import("../lib/legacyPaths");
    const legacy = legacyRedirect(location.pathname);
    if (legacy)
      throw redirect({
        href: legacy,
        statusCode: 301,
        // Cacheable, unlike the host redirect below: these are keyed on the
        // path, which is what Workers Caching keys on, and they will not change
        // until the pages they point at are written.
        headers: { "cache-control": "public, max-age=86400" },
      });

    const { canonicalHostRedirect } = await import("../lib/canonicalHost");
    const href = canonicalHostRedirect(location.pathname, location.searchStr);
    if (href)
      throw redirect({
        href,
        statusCode: 301,
        // NOT cacheable, and this is load-bearing rather than cautious.
        // Workers Caching keys on the path and not the host (see the `cache`
        // block in wrangler.jsonc), so a cached `www` redirect for `/` would be
        // handed to apex visitors as a redirect to the page they are already
        // on — an infinite bounce, served from cache, for everyone.
        headers: { "cache-control": "no-store" },
      });
  },
  // Cache-Control and HSTS for the pages that reach here without a route of
  // their own — chiefly the 404 below. Every real route sets its own.
  headers: documentHeaders,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      // The canvas — the browser chrome tint on Android and in a phone's task
      // switcher. It matches the page rather than the accent so the chrome reads
      // as an extension of the page instead of a coloured band above it.
      //
      // The warm cream from `@fazly/theme` (`surface.page`). It was `#07080B`,
      // which is the reference app's near-black: the page under it is cream, so
      // Android drew a black band above a cream page on every screen.
      { name: "theme-color", content: "#FBF9F4" },
      // Light only, matching the app — DESIGN-SYSTEM §9 pins
      // `userInterfaceStyle: "light"` and the mobile client obeys it. This said
      // `dark`, which asked the browser to render form controls, scrollbars and
      // its own UA styles dark around a deliberately light page; the scrollbar
      // was the part people noticed. A shared link opened from the app has to
      // look like the app it came from.
      { name: "color-scheme", content: "light" },
      // Bing Webmaster Tools ownership. Not a secret and not removable once
      // verification is done — Bing re-checks it, and a site that stops
      // asserting ownership silently loses the property along with its data.
      //
      // Here on the ROOT rather than only on the home page, which is what Bing
      // asks for, because every page of this site is server-rendered by the
      // same shell and one place is cheaper to keep true than nine.
      //
      // Google's equivalent is a DNS TXT record on the zone, not a tag — the
      // two verifications live in different places on purpose.
      { name: "msvalidate.01", content: "F4A164AACC9E88923171A9E651FB36EF" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // These were Bible Shield's blue shield-and-cross until the icons were
      // regenerated: the whole visual system came across from that app and the
      // icons came with it, so a looksmaxxing site was serving a CROSS in the
      // tab, in bookmarks and next to its title in Google.
      //
      // They are now THE APP'S OWN ICON — the rim-lit head — derived from
      // `apps/mobile/assets/icon.png` by `scripts/derive-web-icons.py`. One
      // master for both surfaces, so a visitor who has the app installed
      // recognises the tab favicon as the thing on their home screen.
      // `public/logo.png` is a 1024 copy of the same render rather than a source
      // of its own: the v1 site published that URL and every address v1
      // published still resolves.
      //
      // The `.ico` is the one asset that is NOT a straight resize. At the 16px a
      // tab actually draws, the full render collapses — the head is mostly
      // shadow and what survives is a purple ring with a grey smudge in it — so
      // it crops in on the head and drops the reticle, which is decoration at
      // that size and noise at 16. It carries 16/32/48 in one file so the
      // browser picks a size rather than downsampling a 48 itself.
      //
      // They also have to exist at all. A browser with no icon requests
      // /favicon.ico, gets the app shell's HTML back with a 200, and shows the
      // blank-page glyph in all three places.
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      {
        rel: "icon",
        href: "/icon-192.png",
        type: "image/png",
        sizes: "192x192",
      },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // The two faces above the fold. Preloaded because `font-display: swap`
      // means the alternative is a visible reflow from the fallback stack on
      // the first paint of every page.
      //
      // Only the `latin` subsets. `latin-ext` carries the Turkish, German,
      // Spanish and Portuguese accents and is discovered from the stylesheet's
      // `unicode-range` when a page actually contains one — preloading both
      // would download 125 KB on every page to save a reflow on four locales.
      //
      // These pointed at `inter-latin` and `intertight-latin`, which is the
      // reference app's pair: the site preloaded two faces the stylesheet never
      // declared, and rendered in neither of the two it did.
      {
        rel: "preload",
        href: "/fonts/baloo2-600.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/nunito-400.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootDocument,
  errorComponent: RootError,
  notFoundComponent: RootNotFound,
});

/**
 * The 404.
 *
 * Reached by a mistyped language segment (`/zz`), a stale share link, and
 * whatever else the internet points at a domain. Without it the router renders
 * its own bare "Not Found" — on our domain, in our type-less default font, as
 * somebody's first impression.
 *
 * English only, and that is the honest choice rather than a shortcut: a 404 has
 * no language of its own to be in. The visitor is one link from a page that is
 * in all nine.
 */
function RootNotFound() {
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        404
      </p>
      <h1 className="font-display text-[clamp(1.75rem,6vw,2.5rem)] leading-tight font-bold tracking-tight text-ink">
        This page does not exist
      </h1>
      <p className="leading-relaxed text-body">
        The link may be old, or the address may have a typo in it.
      </p>
      <a
        href="/"
        className="rim mt-2 inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
      >
        go to the home page
      </a>
    </main>
  );
}

/**
 * What a visitor sees when a route throws.
 *
 * Without it TanStack Router's default takes over, which on a production build
 * is a bare unstyled message — served under our own domain, to someone who
 * arrived from a link a friend sent them, as their first impression of the
 * product. The page is server-rendered, so the throw itself is already recorded
 * in Workers Logs (see the `observability` block in wrangler.jsonc); this is
 * only the half the visitor sees.
 *
 * Kept to plain elements and inline styles on purpose: a stylesheet that failed
 * to load is one of the things that can put a visitor here.
 */
function RootError({ error }: { error: Error }) {
  // Server-side this reaches Workers Logs; in a browser it reaches the console.
  // Either way it is the only line that names WHICH route died.
  console.error({
    level: "error",
    event: "web.route_error",
    error: error.message,
  });
  return (
    <main
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        padding: "2rem",
        textAlign: "center",
        // Inline styles and the system font on purpose: this renders when the
        // app has ALREADY failed, and the stylesheet is one of the things that
        // may not have loaded. A themed error page that needs the theme to look
        // right is an error page that shows black-on-black.
        fontFamily: "system-ui, sans-serif",
        background: "#07080B",
        color: "#F8FAFC",
      }}
    >
      <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>
        Something went wrong
      </h1>
      <p style={{ color: "#8A909C", maxWidth: "36rem" }}>
        This page could not be loaded. Try again, or open Fazly on your phone.
      </p>
      <a href="/" style={{ color: "#5570FF", textDecoration: "underline" }}>
        Go to the home page
      </a>
    </main>
  );
}

/**
 * The document shell, with analytics wrapped around it.
 *
 * `PostHogProvider` is SAFE TO RENDER ON THE SERVER even though posthog-js is a
 * browser library: it only calls `init` from a `useEffect`, which the Worker
 * never runs. That is what lets it sit here in the shell rather than behind a
 * mounted-yet guard, so every route is inside it from the first client render
 * and no pageview is lost to hydration timing.
 *
 * NOT MOUNTED AT ALL when analytics is off, rather than mounted-and-silent. The
 * `analyticsEnabled` guard inside `useTrack` only covers events WE send; the
 * SDK's own automatic pageview capture answers to `init`, not to us. Mounting
 * the provider on a dev server would therefore have filed every localhost
 * navigation and every hot reload into the same project the install funnel is
 * read from — which, on a site with this little traffic, would be most of the
 * data in it.
 */
/**
 * Starts Core Web Vitals collection once the page is in a browser.
 *
 * A component rather than a call inside `RootDocument`, so it can use the
 * PostHog client from context and so React owns the teardown. It renders
 * nothing and it is inside the provider on purpose — outside it, `usePostHog`
 * returns undefined and the metrics go nowhere.
 */
function Vitals() {
  const posthog = usePostHog();
  useEffect(() => {
    if (!posthog) return;

    // Tag the session with the assistant that sent this visitor, if one did.
    // A super property rather than an event, so every later event carries it —
    // see `aiReferrerOf` for why that is the question worth being able to ask.
    const ai = aiReferrerOf(document.referrer);
    if (ai) posthog.register({ ai_referrer: ai });

    // And whether the link it followed was one that exists only in /llms.txt
    // or /llms-full.txt — the one first-party answer to "did the assistant
    // find us through those files or through the page itself". Registered the
    // same way as `ai_referrer`, so both are first-touch for the device.
    if (arrivedViaLlms(window.location.hash)) {
      posthog.register({ llms_link: true });
    }

    return collectVitals(posthog);
  }, [posthog]);
  return null;
}

/**
 * Offers the site's tools to an AI agent driving this tab, through WebMCP —
 * on the few browsers that have it, and as a no-op everywhere else. See
 * `lib/agents/webmcp.ts` for which tools and why only those.
 *
 * Outside the analytics provider on purpose: it has nothing to do with
 * measurement, and a build with analytics off is still a site an agent can use.
 */
function AgentTools() {
  const router = useRouter();
  useEffect(
    () =>
      registerAgentTools((href) => {
        void router.navigate({ href });
      }),
    [router],
  );
  return null;
}

function RootDocument({ children }: { children: ReactNode }) {
  // Read off the location rather than off a route's params, because the two URL
  // shapes that carry a language belong to different routes (`/ko` vs
  // `/privacy?lang=ko`) and the shell sits above both. `documentLang` is the one
  // place that mapping lives; `seo.test.ts` covers it.
  const location = useRouterState({ select: (state) => state.location });
  const lang = documentLang(location.pathname, location.searchStr);

  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body>
        {analyticsEnabled ? (
          <PostHogProvider apiKey={POSTHOG_TOKEN} options={posthogOptions}>
            <Vitals />
            {children}
          </PostHogProvider>
        ) : (
          children
        )}
        <AgentTools />
        <Scripts />
      </body>
    </html>
  );
}
